import { z } from "zod";

// Base contact form schema with common fields
const baseContactSchema = z.object({
  firstName: z
    .string()
    .min(2, { message: "First name must be at least 2 characters." })
    .max(50, { message: "First name must not exceed 50 characters." }),
  lastName: z
    .string()
    .min(2, { message: "Last name must be at least 2 characters." })
    .max(50, { message: "Last name must not exceed 50 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  phone: z
    .string()
    .min(10, { message: "Phone number must be at least 10 digits." })
    .max(15, { message: "Phone number must not exceed 15 digits." })
    .regex(/^\+?[\d\s\-\(\)]+$/, {
      message: "Please enter a valid phone number.",
    }),
});

// General inquiry form schema
export const generalFormSchema = z.object({
  ...baseContactSchema.shape,
  type: z.literal("general"),
  subject: z
    .string()
    .min(5, { message: "Subject must be at least 5 characters." })
    .max(100, { message: "Subject must not exceed 100 characters." })
    .optional(),
  message: z
    .string()
    .min(10, { message: "Message must be at least 10 characters." })
    .max(1000, { message: "Message must not exceed 1000 characters." }),
});

// Feedback form schema
export const feedbackFormSchema = z.object({
  ...baseContactSchema.shape,
  type: z.literal("feedback"),
  relationship: z.enum(["parent", "student", "alumni", "staff"], {
    message: "Please select your relationship to the school.",
  }),
  message: z
    .string()
    .min(10, { message: "Feedback must be at least 10 characters." })
    .max(1000, { message: "Feedback must not exceed 1000 characters." }),
});

// Online admission application schema
export const applicationSchema = z.object({
  // Student Information
  firstName: z
    .string()
    .min(2, { message: "First name must be at least 2 characters." })
    .max(50, { message: "First name must not exceed 50 characters." }),
  lastName: z
    .string()
    .min(2, { message: "Last name must be at least 2 characters." })
    .max(50, { message: "Last name must not exceed 50 characters." }),
  dob: z.string().min(1, { message: "Date of birth is required." }),
  gender: z.enum(["male", "female", "other"], {
    message: "Please select a gender.",
  }),

  // Contact Information
  address: z
    .string()
    .min(4, { message: "Address must be at least 4 characters." })
    .max(200, { message: "Address must not exceed 200 characters." }),
  city: z
    .string()
    .min(2, { message: "City must be at least 2 characters." })
    .max(50, { message: "City must not exceed 50 characters." }),
  district: z
    .string()
    .min(2, { message: "District must be at least 2 characters." })
    .max(50, { message: "District must not exceed 50 characters." }),
  nationality: z
    .string()
    .min(2, { message: "Nationality must be at least 2 characters." })
    .max(50, { message: "Nationality must not exceed 50 characters." }),

  // Parent/Guardian Information
  fatherName: z
    .string()
    .min(2, { message: "Father's name must be at least 2 characters." })
    .max(100, { message: "Father's name must not exceed 100 characters." }),
  fatherPhone: z
    .string()
    .min(10, { message: "Father's phone must be at least 10 digits." })
    .max(15, { message: "Father's phone must not exceed 15 digits." })
    .regex(/^\+?[\d\s\-\(\)]+$/, {
      message: "Please enter a valid phone number.",
    }),
  fatherOccupation: z
    .string()
    .min(2, { message: "Father's occupation must be at least 2 characters." })
    .max(100, {
      message: "Father's occupation must not exceed 100 characters.",
    }).optional(),
  motherName: z
    .string()
    .min(2, { message: "Mother's name must be at least 2 characters." })
    .max(100, { message: "Mother's name must not exceed 100 characters." }),
  motherPhone: z
    .string()
    .min(10, { message: "Mother's phone must be at least 10 digits." })
    .max(15, { message: "Mother's phone must not exceed 15 digits." })
    .regex(/^\+?[\d\s\-\(\)]+$/, {
      message: "Please enter a valid phone number.",
    }),
  motherOccupation: z
    .string()
    .min(2, { message: "Mother's occupation must be at least 2 characters." })
    .max(100, {
      message: "Mother's occupation must not exceed 100 characters.",
    }).optional(),
  email: z.string().email({ message: "Please enter a valid email address." }),
  guardianName: z
    .string()
    .max(100, { message: "Guardian's name must not exceed 100 characters." })
    .optional(),
  guardianPhone: z
    .string()
    .max(15, { message: "Guardian's phone must not exceed 15 digits." })
    .regex(/^(\+?[\d\s\-\(\)]+)?$/, {
      message: "Please enter a valid phone number.",
    })
    .optional(),
  guardianRelation: z
    .string()
    .max(50, { message: "Guardian's relation must not exceed 50 characters." })
    .optional(),

  // Academic Information
  gradeApplying: z.enum(
    ["nursery", "lkg", "ukg", "1", "2", "3", "4", "5", "6", "7", "8"],
    {
      message: "Please select a grade.",
    }
  ),
  previousSchool: z
    .string()
    .max(200, {
      message: "Previous school name must not exceed 200 characters.",
    })
    .optional(),
  lastGradeCompleted: z
    .string()
    .max(50, { message: "Previous grade must not exceed 50 characters." })
    .optional(),
  academicYear: z
    .string()
    .regex(/^[0-9]{4}$/, {
      message: "Academic year must be in the format YYYY",
    }),
  hasSiblings: z.enum(["yes", "no"]),

  // Emergency Contact
  emergencyContactName: z
    .string()
    .min(2, {
      message: "Emergency contact name must be at least 2 characters.",
    })
    .max(100, {
      message: "Emergency contact name must not exceed 100 characters.",
    }),
  emergencyPhone: z
    .string()
    .min(10, { message: "Emergency contact phone must be at least 10 digits." })
    .max(15, { message: "Emergency contact phone must not exceed 15 digits." })
    .regex(/^\+?[\d\s\-\(\)]+$/, {
      message: "Please enter a valid phone number.",
    }),
  relationship: z
    .string()
    .min(2, {
      message: "Emergency contact relation must be at least 2 characters.",
    })
    .max(50, {
      message: "Emergency contact relation must not exceed 50 characters.",
    }),

  // Additional Information
  healthInfo: z
    .string()
    .max(500, { message: "Health information must not exceed 500 characters." })
    .optional(),
  additionalInfo: z
    .string()
    .max(500, {
      message: "Additional information must not exceed 500 characters.",
    })
    .optional(),

  // Terms and Conditions
  terms: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions.",
  }),
});

// Testimonial form schema
export const testimonialSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Name must be at least 2 characters." })
    .max(100, { message: "Name must not exceed 100 characters." }),
  relationship: z.enum(["parent", "student", "alumni", "staff", "other"], {
    message: "Please select your relationship to the school.",
  }),
  role: z
    .string()
    .min(2, { message: "Role/Position must be at least 2 characters." })
    .max(100, { message: "Role/Position must not exceed 100 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  testimonial: z
    .string()
    .min(50, { message: "Testimonial must be at least 50 characters." })
    .max(1000, { message: "Testimonial must not exceed 1000 characters." }),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must consent to sharing your testimonial.",
  }),
  rating: z
    .number()
    .min(1, { message: "Rating must be at least 1." })
    .max(5, { message: "Rating must not exceed 5." })
    .optional(),
  year: z
    .number()
    .min(2000, { message: "Year must be at least 2000." })
    .max(new Date().getFullYear(), { message: "Year cannot be in the future." })
    .optional(),
});

// Support form schema matching API expectations
export const supportFormSchema = z.object({
  inquiryType: z.enum(["volunteer", "donation", "sponsorship"], {
    message: "Please select an inquiry type.",
  }),
  name: z
    .string()
    .min(2, { message: "Name must be at least 2 characters." })
    .max(100, { message: "Name must not exceed 100 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  phone: z
    .string()
    .max(15, { message: "Phone number must not exceed 15 digits." })
    .regex(/^\+?[\d\s\-\(\)]+$/, {
      message: "Please enter a valid phone number.",
    })
    .optional(),
  country: z
    .string()
    .min(2, { message: "Country is required." })
    .max(100, { message: "Country must not exceed 100 characters." }),
  organization: z
    .string()
    .max(200, { message: "Organization must not exceed 200 characters." })
    .optional(),
  organizationType: z
    .string()
    .max(100, { message: "Organization type must not exceed 100 characters." })
    .optional(),
  website: z
    .string()
    .url({ message: "Please enter a valid website URL." })
    .optional()
    .or(z.literal("")),
  message: z
    .string()
    .min(20, { message: "Message must be at least 20 characters." })
    .max(2000, { message: "Message must not exceed 2000 characters." }),

  // Volunteer-specific fields
  volunteerType: z
    .string()
    .transform((val) => (val === "" ? undefined : val))
    .pipe(z.enum(["remote", "onsite", "both"]).optional()),
  availableTime: z
    .string()
    .max(500, { message: "Available time must not exceed 500 characters." })
    .optional(),
  skills: z.array(z.string()).optional(),

  // Donation-specific fields
  donationAmount: z
    .string()
    .max(50, { message: "Donation amount must not exceed 50 characters." })
    .optional(),
  donationFrequency: z
    .string()
    .transform((val) => (val === "" ? undefined : val))
    .pipe(z.enum(["once", "monthly", "annually"]).optional()),
  interests: z.array(z.string()).optional(),

  // General fields
  urgency: z
    .string()
    .transform((val) => (val === "" ? undefined : val))
    .pipe(
      z.enum(["immediate", "short", "medium", "long", "exploring"]).optional()
    ),
  newsletter: z.boolean().optional(),
});

// Export TypeScript types
export type GeneralFormValues = z.infer<typeof generalFormSchema>;
export type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;
export type ApplicationFormValues = z.infer<typeof applicationSchema>;
export type TestimonialFormValues = z.infer<typeof testimonialSchema>;
export type SupportFormValues = z.infer<typeof supportFormSchema>;

// Export all schemas as a collection for easier management
export const contactSchemas = {
  general: generalFormSchema,
  feedback: feedbackFormSchema,
  application: applicationSchema,
  testimonial: testimonialSchema,
  support: supportFormSchema,
} as const;
